import React from 'react';
import { StyleSheet } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Animated, { FadeIn, useAnimatedRef, useScrollViewOffset } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';

import { SheetScreen } from '@/components/ScreenComponent';
import { EVENT_SHEET_TITLE_HEIGHT } from '@/components/event-sheet/EventSheetTitle';
import { Box } from '@/components/lib/components/Box';
import { EmptyHandle, MODAL_HANDLE_HEIGHT } from '@/feature/alerts/components/Modal';
import { ProjectionsList } from '@/feature/player-profile-sheets/ProjectionsList';
import { PlayerProfileHeader } from '@/feature/player-profile-sheets/components/PlayerProfileHeader';
import { PlayerProfileTitle } from '@/feature/player-profile-sheets/components/PlayerProfileTitle';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { RootStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { isIos, showScrollIndicator } from '@/utils/constants-platform-specific';

// use react-native-gesture-handler's ScrollView wrapped with createAnimatedComponent to avoid gesture conflicts on Android
// Animated.ScrollView causes gesture conflicts with gestureDirection='vertical', so it can't coordinate with the nav gesture handler
const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);

/**
 * Screen for displaying the single player profile for pickslip
 * Using a screen instead of using the modal directly to allow for the custom modal animations
 * of the previous screen
 */
export const SinglePlayerProfileModalScreen = () => {
    const route = useRoute<RouteProp<RootStackParamList, 'SinglePlayerProfileModalScreen'>>();
    const { player } = route.params;
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();

    const scrollRef = useAnimatedRef<Animated.ScrollView>();
    const scrollOffset = useScrollViewOffset(scrollRef);

    return (
        <SheetScreen style={common.flex} edges={['top']}>
            <MaxWidthWrapper flex={1}>
                <Box
                    position={'absolute'}
                    zIndex={1}
                    width={'100%'}
                    height={EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT}
                    overflow={'hidden'}
                >
                    <Box style={common.flex}>
                        {/*
                         * UX hasn't decided on the handle yet, leaning towards none since native Apple apps
                         * don't have it. empty handle just keeps the spacing, revisit when UX decides
                         */}
                        <EmptyHandle handleContainerStyle={common.backgroundColorTransparent} />
                        <PlayerProfileTitle
                            onClose={() => navigation.goBack()}
                            playerInfo={player}
                            fromSinglePlayerProfile={true}
                            scrollOffset={scrollOffset}
                        />
                    </Box>
                </Box>
                <AnimatedScrollView
                    ref={scrollRef}
                    contentContainerStyle={{ paddingBottom: insets.bottom }}
                    showsVerticalScrollIndicator={showScrollIndicator}
                    style={styles.scrollContainer}
                >
                    <PlayerProfileHeader scrollOffset={scrollOffset} playerInfo={player} />
                    <Animated.View entering={isIos ? FadeIn.duration(100) : undefined} style={common.zIndexNegative1}>
                        <Box px={'s16'} paddingTop={'s16'} height={'100%'} paddingBottom={'s24'}>
                            <ProjectionsList playerWithTeamAndEvent={player} />
                        </Box>
                    </Animated.View>
                </AnimatedScrollView>
            </MaxWidthWrapper>
        </SheetScreen>
    );
};

const styles = StyleSheet.create({
    scrollContainer: {
        minHeight: '100%',
    },
});
